export interface SettingField {
  content: string
  labelKey: string
  placeholderKey: string
  descriptionKey: string
  type?: 'text' | 'password'
}

export interface UserSettings {
  openaiApiKey: SettingField
  selectedOpenaiModel: SettingField
  anthropicApiKey: SettingField
  selectedAnthropicModel: SettingField
  googleApiKey: SettingField
  selectedGoogleModel: SettingField
  groqApiKey: SettingField
  selectedGroqModel: SettingField
  openrouterApiKey: SettingField
  selectedOpenrouterModel: SettingField
  selectedProvider: SettingField
  webSearchEnabled: SettingField
  selectedThemeId: SettingField
  selectedIdentityId: SettingField
}

const createDefaultSettings = (): UserSettings => ({
  openaiApiKey: {
    content: '',
    labelKey: 'settings.apiKeys.openaiLabel',
    placeholderKey: 'settings.apiKeys.openaiPlaceholder',
    descriptionKey: 'settings.apiKeys.openaiDescription',
    type: 'password',
  },
  anthropicApiKey: {
    content: '',
    labelKey: 'settings.apiKeys.anthropicLabel',
    placeholderKey: 'settings.apiKeys.anthropicPlaceholder',
    descriptionKey: 'settings.apiKeys.anthropicDescription',
    type: 'password',
  },
  googleApiKey: {
    content: '',
    labelKey: 'settings.apiKeys.googleLabel',
    placeholderKey: 'settings.apiKeys.googlePlaceholder',
    descriptionKey: 'settings.apiKeys.googleDescription',
    type: 'password',
  },
  groqApiKey: {
    content: '',
    labelKey: 'settings.apiKeys.groqLabel',
    placeholderKey: 'settings.apiKeys.groqPlaceholder',
    descriptionKey: 'settings.apiKeys.groqDescription',
    type: 'password',
  },
  openrouterApiKey: {
    content: '',
    labelKey: 'settings.apiKeys.openrouterLabel',
    placeholderKey: 'settings.apiKeys.openrouterPlaceholder',
    descriptionKey: 'settings.apiKeys.openrouterDescription',
    type: 'password',
  },
  selectedOpenaiModel: {
    content: '',
    labelKey: 'settings.models.selectedOpenaiLabel',
    placeholderKey: '',
    descriptionKey: 'settings.models.selectedOpenaiDescription',
  },
  selectedAnthropicModel: {
    content: '',
    labelKey: 'settings.models.selectedAnthropicLabel',
    placeholderKey: '',
    descriptionKey: 'settings.models.selectedAnthropicDescription',
  },
  selectedGoogleModel: {
    content: '',
    labelKey: 'settings.models.selectedGoogleLabel',
    placeholderKey: '',
    descriptionKey: 'settings.models.selectedGoogleDescription',
  },
  selectedGroqModel: {
    content: '',
    labelKey: 'settings.models.selectedGroqLabel',
    placeholderKey: '',
    descriptionKey: 'settings.models.selectedGroqDescription',
  },
  selectedOpenrouterModel: {
    content: '',
    labelKey: 'settings.models.selectedOpenrouterLabel',
    placeholderKey: '',
    descriptionKey: 'settings.models.selectedOpenrouterDescription',
  },
  selectedProvider: {
    content: '',
    labelKey: 'settings.selectedProvider',
    placeholderKey: '',
    descriptionKey: 'settings.selectedProviderDescription',
  },
  webSearchEnabled: {
    content: 'false',
    labelKey: 'settings.webSearchEnabled',
    placeholderKey: '',
    descriptionKey: 'settings.webSearchEnabledDescription',
  },
  selectedThemeId: {
    content: '',
    labelKey: 'settings.selectedThemeId',
    placeholderKey: '',
    descriptionKey: 'settings.selectedThemeIdDescription',
  },
  selectedIdentityId: {
    content: '',
    labelKey: 'settings.selectedIdentityId',
    placeholderKey: '',
    descriptionKey: 'settings.selectedIdentityIdDescription',
  },
})

export const clearUserSettings = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('settings')
  }
}

export const extractSettingsContent = (
  settings: UserSettings
): Record<string, string> => {
  const content: Record<string, string> = {}
  Object.entries(settings).forEach(([key, field]) => {
    content[key] = field.content
  })
  return content
}

export const loadSettingsContent = (
  defaultSettings: UserSettings,
  content: Record<string, string>
): UserSettings => {
  const settings = { ...defaultSettings }
  Object.entries(content).forEach(([key, value]) => {
    if (key in settings) {
      const settingKey = key as keyof UserSettings
      ;(settings[settingKey] as SettingField).content = value
    }
  })
  return settings
}

export const getUserSettings = (): UserSettings => {
  const defaultSettings = createDefaultSettings()

  if (typeof window !== 'undefined') {
    const savedContent = localStorage.getItem('settings')
    if (savedContent) {
      try {
        const content = JSON.parse(savedContent)
        return loadSettingsContent(defaultSettings, content)
      } catch (error) {
        console.error('Failed to parse saved settings:', error)
      }
    }
  }

  return defaultSettings
}

const getModelFieldKey = (provider: string): keyof UserSettings | null => {
  switch (provider) {
    case 'openai':
      return 'selectedOpenaiModel'
    case 'anthropic':
      return 'selectedAnthropicModel'
    case 'google':
      return 'selectedGoogleModel'
    case 'groq':
      return 'selectedGroqModel'
    case 'openrouter':
      return 'selectedOpenrouterModel'
    default:
      return null
  }
}

export const saveSelectedModel = (provider: string, modelId: string): void => {
  if (typeof window !== 'undefined') {
    const settings = getUserSettings()
    const fieldKey = getModelFieldKey(provider)

    if (fieldKey) {
      settings[fieldKey].content = modelId
      const content = extractSettingsContent(settings)
      localStorage.setItem('settings', JSON.stringify(content))
    }
  }
}

export const getSelectedModel = (provider: string): string | undefined => {
  const settings = getUserSettings()
  const fieldKey = getModelFieldKey(provider)

  if (fieldKey) {
    return settings[fieldKey].content || undefined
  }

  return undefined
}

export const saveSelectedIdentity = (identityId: string): void => {
  localStorage.setItem('identity-id', identityId)
}

export const getSelectedIdentity = (): string => {
  const identityId = localStorage.getItem('identity-id')
  return identityId || 'default'
}

export const saveSelectedProvider = (provider: string): void => {
  if (typeof window !== 'undefined') {
    const settings = getUserSettings()
    settings.selectedProvider.content = provider
    const content = extractSettingsContent(settings)
    localStorage.setItem('settings', JSON.stringify(content))
  }
}

export const getSelectedProvider = (): string | undefined => {
  const settings = getUserSettings()
  return settings.selectedProvider.content || undefined
}

export const saveWebSearchEnabled = (enabled: boolean): void => {
  if (typeof window !== 'undefined') {
    const settings = getUserSettings()
    settings.webSearchEnabled.content = enabled.toString()
    const content = extractSettingsContent(settings)
    localStorage.setItem('settings', JSON.stringify(content))
  }
}

export const getWebSearchEnabled = (): boolean => {
  const settings = getUserSettings()
  return settings.webSearchEnabled.content === 'true'
}

export const saveSelectedThemeId = (themeId: string): void => {
  if (typeof window !== 'undefined') {
    const settings = getUserSettings()
    settings.selectedThemeId.content = themeId
    const content = extractSettingsContent(settings)
    localStorage.setItem('settings', JSON.stringify(content))
  }
}

export const getSelectedThemeId = (): string | undefined => {
  const settings = getUserSettings()
  return settings.selectedThemeId.content || undefined
}

// Cache theme JSON for instant loading without API fetch
export const cacheThemeJson = (themeJson: any): void => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem('kyogre-cached-theme', JSON.stringify(themeJson))
    } catch (error) {
      console.warn('Failed to cache theme JSON:', error)
    }
  }
}

export const getCachedThemeJson = (): any | null => {
  if (typeof window !== 'undefined') {
    try {
      const cached = localStorage.getItem('kyogre-cached-theme')
      return cached ? JSON.parse(cached) : null
    } catch (error) {
      console.warn('Failed to retrieve cached theme JSON:', error)
      return null
    }
  }
  return null
}
